import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import ViewEmployeeDataModal from '../../../modals/ViewEmployeeDataModal'
import { DATA_KEYS, TONE } from '../../data/constants'
import { getDataGap } from '../../data/reportModel'
import EmployeeUserCell from '../../components/EmployeeUserCell/EmployeeUserCell'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import { getDiscoverUnavailableMessageId } from './discoverAvailability'
import styles from './ShadowAiUsers.module.scss'

// Discover emits ISO timestamps; the mock uses friendly relative strings. Show the date when it
// parses, otherwise pass the value through so both render sensibly.
const formatLastActive = (value) => {
  if (!value) return '—'
  const time = Date.parse(value)
  return Number.isNaN(time) ? value : new Date(time).toLocaleDateString()
}

// Only list a handful of app names inline; the rest collapse into a "…" that reveals them on hover,
// so a heavy user's row stays readable.
const MAX_APPS_SHOWN = 5

function UnsanctionedAppsCell({ row, isExportMode }) {
  const names = (row.apps ?? []).map((app) => app?.name || app?.url).filter(Boolean)
  const shown = names.slice(0, MAX_APPS_SHOWN)
  const overflow = names.slice(MAX_APPS_SHOWN)
  const visibleNames = shown.length ? shown.join(', ') : '—'

  // PDF export: a hover tooltip is useless, so cap the list with a static "+N" for the remainder.
  if (isExportMode) {
    return (
      <span className="text-body">
        {visibleNames}
        {overflow.length > 0 && ` +${overflow.length}`}
      </span>
    )
  }

  // UI: cap the list and reveal the rest via a hover tooltip on a "…".
  return (
    <span className="text-body">
      {visibleNames}
      {overflow.length > 0 && (
        <>
          {', '}
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`shadow-ai-user-apps-${row.key}`}>{overflow.join(', ')}</Tooltip>}
          >
            <button type="button" className={styles.moreApps} aria-label={`${overflow.length} more`}>
              …
            </button>
          </OverlayTrigger>
        </>
      )}
    </span>
  )
}

UnsanctionedAppsCell.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string,
    apps: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, url: PropTypes.string })),
  }).isRequired,
  isExportMode: PropTypes.bool.isRequired,
}

const buildColumns = (openEmployee, isExportMode) => [
  {
    key: 'name',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.COL_NAME" />,
    // Clickable name opens the user's employee details modal; static plain text in export mode.
    render: (row) => {
      const displayName = row.name || row.user || row.profile
      if (!displayName) return <span className="text-muted">—</span>
      return <EmployeeUserCell row={row} onOpen={openEmployee} displayName={displayName} />
    },
    sortable: true,
    sortValue: (row) => (row.name || row.user || row.profile || '').toLowerCase(),
  },
  {
    key: 'email',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.COL_EMAIL" />,
    render: (row) => <span className={row.user ? 'text-body' : 'text-muted'}>{row.user || '—'}</span>,
    sortable: true,
    sortValue: (row) => (row.user || '').toLowerCase(),
  },
  {
    key: 'apps',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.COL_APPS" />,
    render: (row) => <UnsanctionedAppsCell row={row} isExportMode={isExportMode} />,
  },
  {
    key: 'appCount',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.COL_APP_COUNT" />,
    render: (row) => <span className={styles.countValue}>{row.appCount ?? row.apps?.length ?? 0}</span>,
    sortable: true,
    sortValue: (row) => row.appCount ?? row.apps?.length ?? 0,
  },
  {
    key: 'lastSeen',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.COL_LAST_ACTIVE" />,
    render: (row) => <span className="text-body">{formatLastActive(row.lastSeen)}</span>,
    sortable: true,
    sortValue: (row) => row.lastSeen || '',
  },
]

// Table-only: rendered inside a SubArea of the Shadow AI SubBlock (see ShadowAi.jsx).
export default function ShadowAiUsers({ usersData = null, dataAvailability = [], gap = null }) {
  const selectedCustomer = useSelector(getSelectedCustomer)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const isExportMode = useAIRiskReportExportMode()
  const openEmployee = useCallback((employee) => setSelectedEmployee(employee), [])
  const closeEmployee = useCallback(() => setSelectedEmployee(null), [])

  const dataGap = getDataGap(dataAvailability, [DATA_KEYS.DISCOVERED_AI_APP_USERS]) ?? gap
  const isAssessed = !dataGap
  const rows = (usersData?.items ?? []).map((row) => ({ ...row, key: row.entityId || row.user }))
  const total = usersData?.total ?? rows.length
  const columns = buildColumns(openEmployee, isExportMode)

  const footer =
    total > rows.length ? (
      <span className={styles.footnote}>
        <FormattedMessage
          id="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.SHOWING_TOP"
          values={{ shown: rows.length, total }}
        />
      </span>
    ) : null

  return (
    <div className={styles.tableWrap}>
      <ReportTable
        columns={columns}
        rows={rows}
        footer={footer}
        empty={
          <EmptyState
            messageId={
              isAssessed
                ? 'AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.EMPTY'
                : getDiscoverUnavailableMessageId(dataGap.reason)
            }
            values={{ dataType: 'Shadow AI users' }}
            tone={isAssessed ? TONE.OK : TONE.NEUTRAL}
          />
        }
      />
      {selectedEmployee && (
        <ViewEmployeeDataModal
          show
          onHide={closeEmployee}
          tableRowEmployee={selectedEmployee}
          selectedCustomer={selectedCustomer}
        />
      )}
    </div>
  )
}

ShadowAiUsers.propTypes = {
  usersData: PropTypes.shape({
    total: PropTypes.number,
    discoverUsersTotal: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
  gap: PropTypes.shape({ reason: PropTypes.string }),
}
